import { Box, Card, Chip, Divider, Grid, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { ReactNode } from 'react'

import {
  DATE_FORMAT,
  ONLY_DATE_TIME_FORMAT,
} from '../../../../../constants/global'
import { numberFormat } from '../../../../../utils'
import { getStatusLabel, SUCCESS_STATUSES } from '../constants'
import { ISaleOrderDetail } from '../types'

interface Props {
  detail: ISaleOrderDetail
}

const statusColor = (
  status: string,
): 'success' | 'error' | 'default' | 'warning' => {
  if (SUCCESS_STATUSES.includes(status)) return 'success'
  if (status === 'CANCELLED' || status === 'CANCEL') return 'error'
  if (status === 'UNPAID' || status === 'ON_HOLD') return 'warning'
  return 'default'
}

const Field = ({ label, value }: { label: string; value: ReactNode }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>
      {value}
    </Typography>
  </Grid>
)

const OrderInfoCard = ({ detail }: Props) => {
  const recipient = detail.raw?.recipient_address ?? {}
  const payment = detail.raw?.payment ?? {}

  return (
    <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          คำสั่งซื้อ #{detail.order_id}
        </Typography>
        <Chip
          size="small"
          label={getStatusLabel(detail.status)}
          color={statusColor(detail.status)}
        />
        <Chip
          size="small"
          variant="outlined"
          label={detail.is_cod ? 'COD' : 'โอน'}
          color={detail.is_cod ? 'warning' : 'info'}
        />
        {detail.cost_missing_count > 0 && (
          <Chip
            size="small"
            variant="outlined"
            color="warning"
            label={`ต้นทุนไม่ครบ ${detail.cost_missing_count} รายการ`}
          />
        )}
      </Box>

      <Grid container spacing={2}>
        <Field
          label="วันที่สั่ง"
          value={
            detail.order_date
              ? dayjs(detail.order_date).format(DATE_FORMAT)
              : '-'
          }
        />
        <Field
          label="วันที่ชำระเงิน"
          value={
            detail.paid_time
              ? dayjs.unix(detail.paid_time).format(ONLY_DATE_TIME_FORMAT)
              : '-'
          }
        />
        <Field label="จำนวนชิ้น" value={numberFormat(detail.quantity, 0)} />
        <Field label="ยอดสินค้า (฿)" value={numberFormat(detail.sub_total)} />
        <Field
          label="ค่าจัดส่ง (฿)"
          value={numberFormat(Number(payment.shipping_fee ?? 0))}
        />
        <Field label="ยอดรวม (฿)" value={numberFormat(detail.total_amount)} />
        <Field
          label="ต้นทุนสินค้า (฿)"
          value={numberFormat(detail.cost_total)}
        />
        <Field
          label="กำไรขั้นต้น (฿)"
          value={numberFormat(detail.total_amount - detail.cost_total)}
        />
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        ผู้รับสินค้า
      </Typography>
      <Grid container spacing={2}>
        <Field label="ชื่อ" value={recipient.name ?? '-'} />
        <Field label="เบอร์โทร" value={recipient.phone_number ?? '-'} />
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">
            ที่อยู่
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {recipient.full_address ?? '-'}
          </Typography>
        </Grid>
      </Grid>
    </Card>
  )
}

export default OrderInfoCard
